import React, { useState } from "react";
import { Modal, Icon, Grid, Checkbox } from "semantic-ui-react";
import { DownloadMetadataModal } from "./DownloadMetadataModal";
import { DownloadFilteredMetadataModal } from "./DownloadFilteredMetadataModal";
import BulkMetadataUpdateModal from "./BulkMetadataUpdateModal";
import { ThinDivider } from "../../../Dashboards/Panel/util";
import { SearchDevicesFiltersType } from "../../../../../BytebeamClient";
import { StyledButtonWithBorder } from "../Devices";

type BulkMetadataOperationsModalProps = {
  readonly isOpen: boolean;
  readonly close: (...args: any[]) => any;
  readonly setLoading: (...args: any[]) => void;
  readonly getDevicesFilters: () => SearchDevicesFiltersType;
  readonly selectedDevices: string[];
  readonly refreshDevices: (inLoop?: boolean) => Promise<void>;
  readonly isRefreshPaused: boolean;
};

function BulkMetadataOperationsModal(props: BulkMetadataOperationsModalProps) {
  const {
    isOpen,
    close,
    setLoading,
    getDevicesFilters,
    selectedDevices,
    refreshDevices,
    isRefreshPaused,
  } = props;
  const [renderBulkDownloadMetadata, setRenderBulkDownloadMetadata] =
    useState(false);
  const [renderFilteredDownloadMetadata, setRenderFilteredDownloadMetadata] =
    useState(false);
  const [renderBulkUpdateMetadata, setRenderBulkUpdateMetadata] =
    useState(false);
  const [isMetadataSelected, setIsMetadataSelected] = useState(true);
  const [isDeviceShadowDataSelected, setIsDeviceShadowDataSelected] =
    useState(false);
  const [isLastActionDataSelected, setIsLastActionDataSelected] =
    useState(false);

  const devicesSearchFilters = getDevicesFilters();

  const BulkOperationButtonStyle = {
    padding: "10px 16px",
    marginBottom: "15px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  } as React.CSSProperties;

  const handleClose = () => {
    setRenderBulkDownloadMetadata(false);
    setRenderFilteredDownloadMetadata(false);
    setRenderBulkUpdateMetadata(false);
    setIsMetadataSelected(true);
    setIsDeviceShadowDataSelected(false);
    setIsLastActionDataSelected(false);
    close();
  };

  return (
    <Modal
      closeIcon
      open={isOpen}
      onClose={handleClose}
      size="tiny"
      className="dark"
    >
      <Modal.Header>Bulk Data Operations</Modal.Header>
      {!renderBulkDownloadMetadata &&
        !renderBulkUpdateMetadata &&
        !renderFilteredDownloadMetadata && (
          <Modal.Content>
            <Grid>
              {/* Bulk Download Section */}
              <Grid.Row>
                <Grid.Column
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: 700 }}>
                    Bulk Device Data Download
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      marginBottom: "16px",
                      opacity: 0.8,
                    }}
                  >
                    Download device data for multiple devices
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    {/* checkboxes to select device shadow data, metadata and device last action data */}
                    <Checkbox
                      label="Metadata"
                      checked={isMetadataSelected}
                      onChange={() =>
                        setIsMetadataSelected(!isMetadataSelected)
                      }
                      disabled={
                        !isLastActionDataSelected && !isDeviceShadowDataSelected
                      }
                    />
                    <Checkbox
                      label="Device Shadow Data"
                      checked={isDeviceShadowDataSelected}
                      onChange={() =>
                        setIsDeviceShadowDataSelected(
                          !isDeviceShadowDataSelected
                        )
                      }
                      disabled={
                        !isMetadataSelected && !isLastActionDataSelected
                      }
                    />
                    <Checkbox
                      label="Last Action Data"
                      checked={isLastActionDataSelected}
                      onChange={() =>
                        setIsLastActionDataSelected(!isLastActionDataSelected)
                      }
                      disabled={
                        !isMetadataSelected && !isDeviceShadowDataSelected
                      }
                    />
                  </div>
                  {(devicesSearchFilters?.stateFilters ||
                    devicesSearchFilters.metaDatafilters ||
                    selectedDevices.length > 0) && (
                    <StyledButtonWithBorder
                      onClick={() => setRenderFilteredDownloadMetadata(true)}
                      style={BulkOperationButtonStyle}
                    >
                      <Icon
                        name="cloud download"
                        style={{ fontSize: "18px" }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "6px",
                        }}
                      >
                        <span>Download Filtered Device Data</span>
                        <span style={{ fontSize: "12px", opacity: 0.8 }}>
                          Download data for filtered devices upto 10,000 devices
                        </span>
                      </div>
                    </StyledButtonWithBorder>
                  )}
                  <StyledButtonWithBorder
                    onClick={() => setRenderBulkDownloadMetadata(true)}
                    style={BulkOperationButtonStyle}
                  >
                    <Icon name="download" style={{ fontSize: "18px" }} />

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "6px",
                      }}
                    >
                      <span>Download All Device Data</span>
                      <span style={{ fontSize: "12px", opacity: 0.8 }}>
                        Download data for all available devices upto 10,000
                        devices
                      </span>
                    </div>
                  </StyledButtonWithBorder>
                </Grid.Column>
              </Grid.Row>

              <ThinDivider />

              {/* Bulk Update Section */}
              <Grid.Row>
                <Grid.Column
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ fontSize: "18px", fontWeight: 700 }}>
                    Bulk Metadata Update
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      marginBottom: "16px",
                      opacity: 0.8,
                    }}
                  >
                    Update metadata for multiple devices at once
                  </div>
                  <StyledButtonWithBorder
                    onClick={() => setRenderBulkUpdateMetadata(true)}
                    style={BulkOperationButtonStyle}
                  >
                    <Icon name="cloud upload" style={{ fontSize: "18px" }} />

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "6px",
                      }}
                    >
                      <span>Update Metadata</span>
                      <span style={{ fontSize: "12px", opacity: 0.8 }}>
                        Update metadata for multiple devices upto 10,000 devices
                      </span>
                    </div>
                  </StyledButtonWithBorder>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Modal.Content>
        )}
      {renderBulkDownloadMetadata && !renderBulkUpdateMetadata && (
        <DownloadMetadataModal
          close={() => setRenderBulkDownloadMetadata(false)}
          isMetadataSelected={isMetadataSelected}
          isDeviceShadowDataSelected={isDeviceShadowDataSelected}
          isLastActionDataSelected={isLastActionDataSelected}
        />
      )}
      {renderFilteredDownloadMetadata && !renderBulkUpdateMetadata && (
        <DownloadFilteredMetadataModal
          devicesSearchFilters={
            selectedDevices.length > 0
              ? { metaDatafilters: { id: selectedDevices } }
              : devicesSearchFilters
          }
          close={() => setRenderFilteredDownloadMetadata(false)}
          isMetadataSelected={isMetadataSelected}
          isDeviceShadowDataSelected={isDeviceShadowDataSelected}
          isLastActionDataSelected={isLastActionDataSelected}
        />
      )}
      {!renderBulkDownloadMetadata && renderBulkUpdateMetadata && (
        <BulkMetadataUpdateModal
          close={() => setRenderBulkUpdateMetadata(false)}
          refreshDevices={refreshDevices}
          isRefreshPaused={isRefreshPaused}
          setLoading={setLoading}
        />
      )}
    </Modal>
  );
}

export default BulkMetadataOperationsModal;
